import { toast } from "sonner";
import { engine } from "@/studio/engine";
import { uploadFile, V } from "@/lib/sessionsApi";
import { apiError } from "@/lib/api";
import { TRACK_COLORS } from "@/studio/songData";

const rid = (p) => p + "_" + Math.random().toString(36).slice(2, 8);
export const isAudioFile = (f) => /^audio\//.test(f.type) || /\.(mp3|wav|aiff?|flac|ogg|m4a|aac|webm)$/i.test(f.name);

export function peaksOf(buf, n = 240) {
  const ch = buf.getChannelData(0), step = Math.max(1, Math.floor(ch.length / n)), out = [];
  for (let i = 0; i < n; i++) { let m = 0; const s = i * step; for (let j = s; j < s + step && j < ch.length; j += 8) { const a = Math.abs(ch[j]); if (a > m) m = a; } out.push(Math.round(m * 100)); }
  return out;
}

export async function importAudioFiles(files, { song, mutate, setSel, startBar, trackId, onProgress }) {
  const list = [...files].filter(isAudioFile);
  if (!list.length) return toast.error("Drop an audio file (mp3, wav, aiff, flac, ogg, m4a)");
  engine.init();
  for (const file of list) {
    const tid = onProgress?.(file.name, 0);
    if (file.size > 250 * 1024 * 1024) { toast.error(`${file.name} is larger than 250 MB`); continue; }
    let stage = "upload";
    try {
      const rec = await uploadFile(file, { tags: ["Studio", song.title], onProgress: (p) => onProgress?.(file.name, Math.min(90, p), tid) });
      onProgress?.(file.name, 95, tid);
      stage = "decode";
      const buf = await engine.loadAudio(rec.id, V.url(rec.id));
      const barDur = (60 / song.bpm) * 4;
      const length = Math.max(1, Math.ceil(buf.duration / barDur - 0.02));
      const start = Math.max(0, Math.floor(startBar ?? 0));
      const clip = { id: rid("c"), name: file.name.replace(/\.[^.]+$/, ""), start, length, notes: [], audio: { fileId: rec.id, name: file.name, duration: buf.duration, peaks: peaksOf(buf), gain: 1 } };
      let target = trackId;
      mutate((d) => {
        if (start + length > d.bars) d.bars = start + length;
        let tr = target ? d.tracks.find((t) => t.id === target && t.instrument === "audio") : null;
        if (!tr) { tr = { id: rid("t"), name: clip.name.slice(0, 18), instrument: "audio", color: TRACK_COLORS[d.tracks.length % TRACK_COLORS.length], volume: 0.8, pan: 0, mute: false, solo: false, arm: false, eq: { low: 0, mid: 0, high: 0 }, eqOn: true, sendRev: 0, sendDly: 0, cutoff: 1, transpose: 0, clips: [] }; d.tracks.push(tr); target = tr.id; }
        tr.clips.push(clip);
      });
      setSel?.({ trackId: target, clipId: clip.id });
      onProgress?.(file.name, 100, tid);
      toast.success(`${file.name} imported (${length} bar${length === 1 ? "" : "s"}) and saved to your Vault`);
    } catch (e) {
      onProgress?.(file.name, -1, tid);
      console.error("audio import failed", stage, e);
      const why = stage === "upload" ? `upload failed — ${e?.message?.startsWith("chunk") ? `${e.message} (${apiError(e, "network error")})` : apiError(e, e?.message || "network error")}` : (e?.message || "could not decode the audio");
      toast.error(`Could not import ${file.name}: ${why}`, { duration: 9000 });
    }
  }
}

export async function preloadSongAudio(song) {
  const ids = new Set();
  song.tracks.forEach((t) => t.clips.forEach((c) => c.audio?.fileId && ids.add(c.audio.fileId)));
  if (!ids.size) return;
  engine.init();
  await Promise.all([...ids].map((id) => engine.loadAudio(id, V.url(id)).catch(() => null)));
}
